import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff } from 'lucide-react';

export default function AuthPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user.onboarded) navigate('/dashboard');
    else if (user && !user.onboarded) navigate('/onboarding');
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        const data = await login(email, password);
        if (data.onboarded) navigate('/dashboard');
        else navigate('/onboarding');
      } else {
        if (!fullName.trim()) { setError('Full name is required'); setLoading(false); return; }
        await register(email, password, fullName);
        navigate('/onboarding');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') setError(detail);
      else if (Array.isArray(detail)) setError(detail.map(e => e.msg || JSON.stringify(e)).join(' '));
      else setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pageStyle = {
    backgroundColor: '#080812',
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
    padding: '16px',
  };

  const cardStyle = {
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '12px',
    padding: '32px',
    width: '100%',
    maxWidth: '420px',
  };

  const inputStyle = {
    backgroundColor: '#1F2937',
    border: '1px solid #374151',
    borderRadius: '8px',
    padding: '12px 14px',
    color: '#F9FAFB',
    fontSize: '14px',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    color: '#9CA3AF',
    fontSize: '14px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  };

  const buttonStyle = {
    backgroundColor: '#AAFF00',
    color: '#080812',
    border: 'none',
    borderRadius: '8px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '700',
    width: '100%',
    cursor: 'pointer',
  };

  return (
    <div style={pageStyle} data-testid="auth-page">
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <img src="/distill-logo.png" alt="Distill" style={{ height: '80px', margin: '0 auto 16px' }} data-testid="auth-logo" />
          <p style={{ color: '#9CA3AF', fontSize: '14px' }}>Turn what your team knows into company knowledge that never leaves.</p>
        </div>

        {/* Auth Card */}
        <div style={cardStyle} data-testid="auth-card">
          <h2 style={{ color: '#F9FAFB', fontSize: '24px', fontWeight: '600', marginBottom: '24px' }}>
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h2>

          {error && (
            <div style={{ marginBottom: '16px', padding: '12px', borderRadius: '8px', backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', fontSize: '14px' }} data-testid="auth-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {!isLogin && (
              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle} htmlFor="fullName">Full Name</label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="John Smith"
                  style={inputStyle}
                  data-testid="auth-fullname-input"
                />
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle} htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                style={inputStyle}
                required
                data-testid="auth-email-input"
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={labelStyle} htmlFor="password">Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min 6 characters"
                  style={inputStyle}
                  required
                  data-testid="auth-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ ...buttonStyle, opacity: loading ? 0.7 : 1 }}
              data-testid="auth-submit-btn"
            >
              {loading ? (isLogin ? 'Signing in...' : 'Creating account...') : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <button
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: '14px', cursor: 'pointer' }}
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
